import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Link, Typography } from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { getCompanyDetail } from "../../../api/companyApi";
import { labelForLocation } from "../../../helper/address";
import { SIDEBAR } from "./sidebarVisual";
import { SidebarCard } from "./SidebarPrimitives";
import CompanySidebarEmptyState from "./CompanySidebarEmptyState";

function isUsableName(name) {
  if (typeof name !== "string") return false;
  const trimmed = name.trim();
  if (!trimmed) return false;
  if (/^\(unknown name\)$/i.test(trimmed)) return false;
  return true;
}

function SimilarCompanyCard({ company }) {
  const location = labelForLocation(company.primaryLocation);
  const href = company.stagedId ? `/company/${company.stagedId}` : undefined;

  return (
    <SidebarCard testId="similar-company-card-v2">
      <Box sx={{ display: "flex", alignItems: "center", gap: SIDEBAR.gap.withinCard, minWidth: 0 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              ...SIDEBAR.type.body,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {company.primaryName}
          </Typography>
          {location ? (
            <Typography sx={{ ...SIDEBAR.type.meta, mt: 0.25 }}>{location}</Typography>
          ) : null}
        </Box>
        {href ? (
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${company.primaryName}`}
            sx={{
              display: "inline-flex",
              color: "text.secondary",
              "&:hover": { color: "primary.main" },
            }}
          >
            <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
          </Link>
        ) : null}
      </Box>
    </SidebarCard>
  );
}

SimilarCompanyCard.propTypes = {
  company: PropTypes.shape({
    stagedId: PropTypes.string,
    primaryName: PropTypes.string,
    primaryLocation: PropTypes.object,
  }).isRequired,
};

/**
 * Similar companies list for V2 — never renders "(Unknown Name)" placeholders.
 */
export default function SimilarCompaniesPanelV2({ ids = [], compact = false }) {
  const [companies, setCompanies] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!ids?.length) {
        if (!cancelled) {
          setCompanies([]);
          setLoaded(true);
        }
        return;
      }

      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const response = await getCompanyDetail(id);
            return response?.data || null;
          } catch (_error) {
            return null;
          }
        })
      );

      if (cancelled) return;

      const usable = results.filter(
        (company) => company && isUsableName(company.primaryName)
      );
      setCompanies(usable);
      setLoaded(true);
    };

    setLoaded(false);
    load();

    return () => {
      cancelled = true;
    };
  }, [ids]);

  if (!loaded) {
    return (
      <Typography sx={{ ...SIDEBAR.type.bodySecondary, py: 2 }}>
        Loading similar companies…
      </Typography>
    );
  }

  if (!companies.length) {
    return (
      <CompanySidebarEmptyState
        variant="similar"
        testId="similar-companies-empty-state"
        compact={compact}
      />
    );
  }

  return (
    <Box
      data-testid="similar-companies-panel-v2"
      data-compact={compact ? "true" : undefined}
      sx={{ display: "flex", flexDirection: "column", gap: SIDEBAR.gap.card }}
    >
      {companies.map((company) => (
        <SimilarCompanyCard
          key={company.stagedId || company.primaryName}
          company={company}
        />
      ))}
    </Box>
  );
}

SimilarCompaniesPanelV2.propTypes = {
  ids: PropTypes.arrayOf(PropTypes.string),
  compact: PropTypes.bool,
};
